import React, { useState } from "react";
import { useCart } from "../context/CartContext";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag, Coins } from "lucide-react";
import Footer from "../components/Footer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function Cart() {
  const { items, removeItem, updateQty, subtotal, count, clear } = useCart();
  const navigate = useNavigate();

  return (
    <div data-testid="cart-page" className="pt-safe-navbar-lg pb-16">
      <div className="max-w-[1100px] mx-auto px-6">
        <div className="mb-8 flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="iq-overline mb-2"><ShoppingBag className="inline h-3 w-3 mr-1" /> Cart</div>
            <h1 className="font-display text-cream text-5xl md:text-6xl tracking-wider">Your reservation</h1>
            <p className="font-serif italic text-cream-dim text-lg mt-1">{count} item{count === 1 ? "" : "s"}. No payment taken yet.</p>
          </div>
          {items.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="cart-clear" className="iq-btn-ghost text-sm">
                  <Trash2 className="h-4 w-4" /> Empty cart
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="cart-clear-dialog" className="bg-panel border border-panel-border text-cream">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display tracking-wider text-cream text-2xl">
                    Empty the cart?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="font-serif italic text-cream-dim">
                    This will remove every item from your reservation. You can always add them back from the tours and city pages.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cart-clear-cancel" className="iq-btn-ghost border border-panel-border">
                    Keep browsing
                  </AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="cart-clear-confirm"
                    onClick={() => clear()}
                    className="iq-btn-primary"
                  >
                    Yes, empty it
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {items.length === 0 ? (
          <div className="iq-card p-16 text-center">
            <ShoppingBag className="h-12 w-12 text-gold/40 mx-auto mb-4" />
            <h2 className="font-display text-cream text-3xl tracking-wider mb-2">An empty platform.</h2>
            <p className="font-serif italic text-cream-dim mb-6 max-w-md mx-auto leading-relaxed">
              Browse the eight tours, or add accommodations from any city page.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link to="/routes" className="iq-btn-primary">Browse tours <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/map" className="iq-btn-secondary">Open atlas <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_340px] gap-6">
            <div className="space-y-3">
              {items.map((it, i) => (
                <div key={`${it.kind}-${it.id}`} data-testid={`cart-item-${i}`} className="iq-card overflow-hidden grid grid-cols-[120px_1fr_auto] gap-0">
                  <div className="relative bg-ink aspect-square">
                    {it.image && <img src={it.image} alt={it.name} className="absolute inset-0 w-full h-full object-cover" />}
                    <div className="absolute top-1 left-1 bg-ink/85 text-cream text-[9px] font-display tracking-widest px-1.5 py-0.5 rounded-sm uppercase">{it.kind}</div>
                  </div>
                  <div className="p-4 min-w-0">
                    <h3 className="font-display text-xl text-cream tracking-wider truncate">{it.name}</h3>
                    {it.sub && <p className="font-serif italic text-cream-dim text-sm mt-0.5 line-clamp-1">{it.sub}</p>}
                    <div className="flex items-center gap-2 mt-3">
                      <button data-testid={`cart-dec-${i}`} onClick={() => updateQty(it.id, it.kind, it.qty - 1)} className="iq-btn-ghost border border-panel-border rounded-sm w-7 h-7 p-0 justify-center">
                        <Minus className="h-3 w-3" />
                      </button>
                      <span data-testid={`cart-qty-${i}`} className="font-display text-cream w-7 text-center">{it.qty}</span>
                      <button data-testid={`cart-inc-${i}`} onClick={() => updateQty(it.id, it.kind, it.qty + 1)} className="iq-btn-ghost border border-panel-border rounded-sm w-7 h-7 p-0 justify-center">
                        <Plus className="h-3 w-3" />
                      </button>
                      <span className="text-cream-dim text-xs ml-2">× €{it.price_eur} {it.kind === "accommodation" ? "/night/person" : "/person"}</span>
                    </div>
                  </div>
                  <div className="p-4 text-right flex flex-col justify-between items-end">
                    <button data-testid={`cart-remove-${i}`} onClick={() => removeItem(it.id, it.kind)} className="text-cream-dim/60 hover:text-accent-red transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <div className="font-display text-gold text-xl">€{(it.price_eur * it.qty).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary rail */}
            <aside className="space-y-4">
              <div className="iq-card p-5 sticky top-24">
                <div className="iq-overline mb-3">Summary</div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-cream-dim">Subtotal</span>
                  <span className="font-display text-cream text-lg">€{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-cream-dim">Booking fee</span>
                  <span className="text-cream-dim">€0.00</span>
                </div>
                <div className="border-t border-panel-border my-3 pt-3 flex items-center justify-between">
                  <span className="font-display text-cream tracking-wider">Total</span>
                  <span className="font-display text-gold text-3xl">€{subtotal.toLocaleString()}</span>
                </div>
                <p className="text-[10px] text-cream-dim/60 leading-relaxed italic mt-3">
                  Estimates per-person. Final invoice (incl. taxes & fees) is confirmed by our conductor before payment is taken.
                </p>
                <button
                  data-testid="cart-checkout"
                  onClick={() => navigate("/checkout")}
                  className="iq-btn-primary w-full mt-4"
                >
                  Proceed to checkout <ArrowRight className="h-4 w-4" />
                </button>
                <Link to="/routes" className="iq-btn-ghost w-full mt-2 text-xs justify-center">
                  Continue browsing
                </Link>
              </div>
              <div className="iq-card p-4 text-xs text-cream-dim/80 flex items-start gap-2">
                <Coins className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                <span>No payment is taken on checkout. We confirm a final quote within one business day, then send a secure payment link.</span>
              </div>
            </aside>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
